import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import "bootstrap/dist/js/bootstrap.bundle.min.js";
import { HeaderFilterProvider, useHeaderFilters } from "../../context/HeaderFilterContext";
import { encryptQuery, decryptQuery } from "../../utils/encryption";
import useDynamicPageSize from "../../useDynamicPageSize";
import { toast, ToastContainer } from "react-toastify";
import Table from "../../core/common/dataTable";
import Footer from "../../Footer";
import dayjs from "dayjs";
import nodeApi from "../../nodeApi";
import { Link, useNavigate, useLocation, useParams } from "react-router-dom";
import CommonSelect from "../../core/common/commonSelect";
import { useIsMobile } from "../../hooks/useIsMobile";
import { useFilterData } from "../../context/FilterContext";
import { REACT_APP_NODE_STATIC } from "../../environment";
import { page } from "../../core/common/selectoption/selectoption";
interface PolicyType {
    id: number;
    title: string;
    year: number;
    file_url: string;
    created_at: string;
}

interface YearsResponse {
    years: number[];
}

type ModalMode = "add" | "edit";

const PolicyDocument: React.FC = () => {
    const isMobile = useIsMobile(991);
    const pageSize = useDynamicPageSize();
    const { setFilters } = useHeaderFilters();
    const navigate = useNavigate();
    const location = useLocation();
    const { encryptedQuery } = useParams<{ encryptedQuery?: string }>();
    const { filtersData } = useFilterData();
    const currentYear = dayjs().year();
    const [viewUrl, setViewUrl] = useState<string | null>(null);
    const [viewData, setViewData] = useState<PolicyType | null>(null);
    const [data, setData] = useState<PolicyType[]>([]);
    const [years, setYears] = useState<number[]>([]);
    const [selectedYear, setSelectedYear] = useState<string>(currentYear.toString());
    const [totalItems, setTotalItems] = useState<number>(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [loading, setLoading] = useState(true);
    const [tableHeight, setTableHeight] = useState<number>(0);
    const navCausedRef = useRef(false);
    const isInitialFetch = useRef(true);
    const [preview, setPreview] = useState<{
        open: boolean;
        url?: string;
    }>({
        open: false,
    });

    const [modalMode, setModalMode] = useState<ModalMode>("add");
    const [formData, setFormData] = useState({
        title: "",
        year: currentYear,
        file: null as File | null,
    });
    const [formError, setFormError] = useState<{
        title?: string;
        year?: string;
        file?: string;
    }>({});
    const [editId, setEditId] = useState<number | null>(null);
    const [deleteId, setDeleteId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const isSubmittingRef = useRef<boolean>(false);
    const resetForm = () => {
        setFormData({
            title: "",
            year: currentYear,
            file: null,
        });

        setFormError({});
        setEditId(null);

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
    };
    const [editingRecord, setEditingRecord] = useState<PolicyType | null>(null);
    /* ================= HEIGHT ================= */

    useLayoutEffect(() => {
        if (pageSize === 0) return;

        const calculateHeight = () => {
            const baseOffset = 194;
            const extraOffset = window.innerWidth <= 991 ? -43 : -81;
            const fixedOffset = baseOffset + extraOffset;
            setTableHeight(window.innerHeight - fixedOffset);
        };

        calculateHeight();
        window.addEventListener("resize", calculateHeight);
        return () => window.removeEventListener("resize", calculateHeight);
    }, [pageSize]);

    /* ================= FETCH YEARS ================= */

    useEffect(() => {
        if (filtersData?.years) {
            setYears(filtersData.years);
        }
    }, [filtersData]);

    useEffect(() => {
        if (!pageSize) return;

        const totalPages = Math.ceil(totalItems / pageSize);

        if (currentPage > totalPages && totalPages > 0) {
            setCurrentPage(totalPages);
        }
    }, [totalItems, pageSize, currentPage]);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);

        navCausedRef.current = true;

        const encrypted = encryptQuery({
            currentPage: page,
            pageSize,
            module: "policyDocument",
            ts: Date.now(), // optional
        });

        navigate(`/policy-document/${encodeURIComponent(encrypted)}`, {
            replace: false,
        });
    };

    const handleReset = () => {
        const defaultYear = currentYear.toString();

        setSelectedYear(defaultYear);
        setCurrentPage(1);

        const encrypted = encryptQuery({
            year: defaultYear,
            currentPage: 1,
            pageSize,
            module: "policyDocument",
            ts: Date.now(),
        });

        navigate(`/policy-document/${encodeURIComponent(encrypted)}`);
    };

    /* ================= FETCH LIST ================= */

    const fetchPolicies = async () => {
        try {
            setLoading(true);

            const res = await nodeApi<any>("/policies/policy-list", {
                method: "POST",
                body: {
                    currentPage,
                    pageSize,
                    year: selectedYear ? Number(selectedYear) : null,
                },
            });

            setData(res?.data || []);
            setTotalItems(res?.meta?.total || 0);
        } catch (err: any) {
            toast.error("Failed to fetch policies");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (!pageSize) return;

        if (isInitialFetch.current) {
            isInitialFetch.current = false;
            return;
        }

        setLoading(true);
        fetchPolicies();
    }, [currentPage, pageSize, selectedYear]);

    useEffect(() => {
        const tooltipTriggerList = [].slice.call(
            document.querySelectorAll('[data-bs-toggle="tooltip"]')
        );
        tooltipTriggerList.map(function (tooltipTriggerEl: any) {
            return new (window as any).bootstrap.Tooltip(tooltipTriggerEl);
        });
    }, [data]);
    /* ================= HEADER FILTER ================= */

    useEffect(() => {
        const PolicyFilters = () => (
            <div className="upper-absolute-class">
                <div className="Holiday-filters d-flex align-items-center justify-content-between flex-wrap gap-2">
                    <div className="d-flex holiday-full-width gap-2">

                        {/* RESET BUTTON */}
                        {selectedYear !== currentYear.toString() && (
                            <button
                                className="btn add_btn_size btn-outline-primary reset-holidays"
                                style={{ maxWidth: 200 }}
                                onClick={() => {
                                    const defaultYear = currentYear.toString();
                                    setSelectedYear(defaultYear);
                                    setCurrentPage(1);
                                    navigate("/policy-document", { replace: false });
                                }}
                            >
                                <i className="ti ti-refresh" />
                            </button>
                        )}

                        {/* YEAR DROPDOWN */}
                        <div className="holiday-flex">
                            <CommonSelect
                                className="select"
                                options={years.map((year) => ({
                                    value: year.toString(),
                                    label: year.toString(),
                                }))}
                                value={
                                    selectedYear
                                        ? {
                                            value: selectedYear,
                                            label: selectedYear,
                                        }
                                        : null
                                }
                                onChange={(selectedOption) => {
                                    const year =
                                        selectedOption?.value || currentYear.toString();

                                    setSelectedYear(year);
                                    setCurrentPage(1);

                                    const encrypted = encryptQuery({
                                        year,
                                        currentPage: 1,
                                        pageSize,
                                        module: "policyDocument",
                                        ts: Date.now(),
                                    });

                                    navigate(
                                        `/policy-document/${encodeURIComponent(encrypted)}`,
                                        { replace: false }
                                    );
                                }}
                                forceFullWidth={isMobile}
                            />
                        </div>

                        {/* ADD BUTTON */}
                        <button
                            className="btn btn-outline-primary add_btn_size holiday-add-margin-right"
                            onClick={() => openModal("add")}
                        >
                            <i className="ti ti-circle-plus" />
                        </button>

                    </div>
                </div>
            </div>
        );

        setFilters(<PolicyFilters />);
        return () => setFilters(null);
    }, [selectedYear, years, isMobile]);

    useEffect(() => {
        if (navCausedRef.current) {
            navCausedRef.current = false;
            return;
        }

        const [, , encoded] = location.pathname.split("/");

        if (!encoded) {
            setSelectedYear(currentYear.toString());
            setCurrentPage(1);
            return;
        }

        try {
            const decoded = decryptQuery(decodeURIComponent(encoded));
            if (decoded?.year) {
                setSelectedYear(decoded.year.toString());
            }
            if (decoded?.currentPage) {
                setCurrentPage(Number(decoded.currentPage));
            }
        } catch {
            setSelectedYear(currentYear.toString());
            setCurrentPage(1);
        }
    }, [location.pathname]);
    /* ================= MODAL ================= */

    const openModal = (mode: ModalMode, record?: PolicyType) => {
        isSubmittingRef.current = false;

        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }

        if (mode === "add") {
            setModalMode("add");
            setEditingRecord(null);
            setEditId(null);

            setFormData({
                title: "",
                year: currentYear,
                file: null,
            });

            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }

        if (mode === "edit" && record) {
            setModalMode("edit");
            setEditId(record.id);
            setEditingRecord(record); // 

            setFormData({
                title: record.title,
                year: record.year,
                file: null, // always reset
            });
        }

        const modal = document.getElementById("policy_modal");
        if (modal) {
            (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
        }
    };

    const hideModal = () => {
        const modal = document.getElementById("policy_modal");
        if (modal && (window as any).bootstrap?.Modal) {
            const instance = (window as any).bootstrap.Modal.getInstance(modal);
            instance?.hide();
        }

        resetForm(); // ensures fresh form next open
    };

    const openDeleteModal = (id: number) => {
        setDeleteId(id);
        const modal = document.getElementById("top-modal");
        if (modal) {
            (window as any).bootstrap.Modal.getOrCreateInstance(modal).show();
        }
    };
    /* ================= SAVE ================= */

    const handleSubmit = async () => {
        if (isSubmittingRef.current) return;

        const errors: any = {};

        if (!formData.title.trim()) {
            errors.title = "Title is required";
        }

        if (!formData.year) {
            errors.year = "Year is required";
        }

        if (modalMode === "add" && !formData.file) {
            errors.file = "PDF file is required";
        }

        if (Object.keys(errors).length > 0) {
            setFormError(errors);
            return;
        }

        isSubmittingRef.current = true;

        const body = new FormData();
        body.append("title", formData.title);
        body.append("year", formData.year.toString());

        if (formData.file) {
            body.append("file", formData.file);
        }

        try {
            await nodeApi(
                modalMode === "add"
                    ? "/policies"
                    : `/policies/${editId}`,
                {
                    method: modalMode === "add" ? "POST" : "PUT",
                    body,
                }
            );

            toast.success(
                modalMode === "add"
                    ? "Policy added successfully"
                    : "Policy updated successfully"
            );

            hideModal();
            fetchPolicies();
            setEditingRecord(null); // clear after successful save
        } catch {
            toast.error("Something went wrong");
        } finally {
            isSubmittingRef.current = false;
        }
    };

    /* ================= DELETE ================= */

    const handleDelete = async (id: number) => {
        try {
            await nodeApi(`/policies/${id}`, { method: "DELETE" });

            toast.success("Deleted successfully");

            // If deleting last item of page, adjust page
            if (data.length === 1 && currentPage > 1) {
                setCurrentPage(currentPage - 1);
            } else {
                fetchPolicies();
            }
        } catch (err) {
            toast.error("Failed to delete policy");
        }
    };

    /* ================= TABLE COLUMNS ================= */

    const columns = [
        //   {
        //     title: "Sr No",
        //     render: (_: any, __: any, index: number) =>
        //       (currentPage - 1) * pageSize + index + 1,
        //   },
        {
            title: "Doc Title",
            render: (_: any, r: PolicyType) => {
                const maxTitleLength = 55;

                const truncateText = (text: string, maxLength: number) =>
                    text && text.length > maxLength
                        ? text.substring(0, maxLength) + "..."
                        : text;

                const titleText = r.title || "-";

                return (
                    <div style={{
                        display: 'grid',      // Grid handles the 'min-width: 0' rule better than block
                        width: '100%',
                        overflow: 'hidden'
                    }}>
                        <span
                            title={titleText}
                            style={{
                                display: "block",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                minWidth: 0
                            }}
                        >
                            {titleText}
                        </span>
                    </div>
                );
            },
        },
        {
            title: "Doc",
            render: (_: any, r: PolicyType) => {
                if (!r.file_url) return null;

                return (
                    <div
                        onClick={() =>
                            setPreview({
                                open: true,
                                url: `${REACT_APP_NODE_STATIC}${r.file_url}`,
                            })
                        }
                        title="Preview Document"
                        style={{ cursor: "pointer" }}
                    >
                        <i className="ti ti-file-text fs-18 text-primary" />
                    </div>
                );
            },
        },
        {
            title: "Uploaded Date",
            render: (_: any, r: PolicyType) =>
                dayjs(r.created_at).format("DD-MMM-YYYY"),
        },
        {
            title: "Actions",
            render: (_: any, r: PolicyType) => (
                <div className="action-icon d-inline-flex">
                    <Link
                        to="#"
                        onClick={(e) => {
                            e.preventDefault();

                            if (!r.file_url) return;

                            const fileUrl = `${REACT_APP_NODE_STATIC}${r.file_url}`;

                            window.open(fileUrl, "_blank");
                        }}
                        title="View Policy"
                    >
                        <i className="ti ti-eye" />
                    </Link>

                    <Link to="#" onClick={() => openModal("edit", r)} title="Edit Policy">
                        <i className="ti ti-edit" />
                    </Link>

                    <Link to="#" onClick={() => openDeleteModal(r.id)} title="Delete Policy">
                        <i className="ti ti-trash" />
                    </Link>
                </div>
            ),
        },
    ];

    return (
        <>
            <HeaderFilterProvider>
                <div className="page-wrapper d-flex flex-column">
                    <div className="content flex-grow-1">
                        <div className="card card-margin-bottom">
                            <div
                                className="card-body p-0"
                                style={{ height: tableHeight, display: "flex", flexDirection: "column" }}
                            >
                                <Table
                                    style={{ flex: 1 }}
                                    dataSource={data}
                                    columns={columns}
                                    currentPage={currentPage}
                                    pageSize={pageSize}
                                    totalItems={totalItems}
                                    onPageChange={handlePageChange}
                                    loading={loading}
                                />
                            </div>
                        </div>
                    </div>

                    {/* MODAL */}
                    <div className="modal fade" id="policy_modal">
                        <div className="modal-dialog modal-md modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h4 className="modal-title">
                                        {modalMode === "add" ? "Add Policy" : "Edit Policy"}
                                    </h4>
                                    <button className="btn-close" onClick={hideModal} />
                                </div>

                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label>Title <span className="text-danger">*</span></label>
                                        <input
                                            className={`form-control ${formError.title ? "is-invalid" : ""}`}
                                            value={formData.title}
                                            onChange={(e) => {
                                                const value = e.target.value;

                                                if (!/^[A-Za-z0-9\s]*$/.test(value)) return;

                                                setFormData((p) => ({ ...p, title: value }));

                                                if (value.trim()) {
                                                    setFormError((p) => ({ ...p, title: "" }));
                                                }
                                            }}
                                            onBlur={() => {
                                                if (!formData.title.trim()) {
                                                    setFormError((p) => ({
                                                        ...p,
                                                        title: "Title is required",
                                                    }));
                                                } else {
                                                    setFormError((p) => ({ ...p, title: "" }));
                                                }
                                            }}
                                        />

                                        {formError.title && (
                                            <div className="invalid-feedback d-block">
                                                {formError.title}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label>Year <span className="text-danger">*</span></label>

                                        <input
                                            type="number"
                                            className={`form-control ${formError.year ? "is-invalid" : ""}`}
                                            value={formData.year}
                                            min={currentYear}
                                            max={currentYear + 1}
                                            onChange={(e) => {
                                                const value = Number(e.target.value);

                                                if (value === currentYear || value === currentYear + 1) {
                                                    setFormData((p) => ({ ...p, year: value }));
                                                    setFormError((p) => ({ ...p, year: "" }));
                                                }
                                            }}
                                            onKeyDown={(e) => {
                                                // Allow only arrow keys + tab
                                                if (
                                                    e.key !== "ArrowUp" &&
                                                    e.key !== "ArrowDown" &&
                                                    e.key !== "Tab"
                                                ) {
                                                    e.preventDefault();
                                                }

                                                if (e.key === "ArrowUp") {
                                                    e.preventDefault();
                                                    setFormData((p) => ({
                                                        ...p,
                                                        year:
                                                            p.year < currentYear + 1
                                                                ? currentYear + 1
                                                                : currentYear + 1,
                                                    }));
                                                }

                                                if (e.key === "ArrowDown") {
                                                    e.preventDefault();
                                                    setFormData((p) => ({
                                                        ...p,
                                                        year:
                                                            p.year > currentYear
                                                                ? currentYear
                                                                : currentYear,
                                                    }));
                                                }
                                            }}
                                        />

                                        {formError.year && (
                                            <div className="invalid-feedback d-block">
                                                {formError.year}
                                            </div>
                                        )}
                                    </div>

                                    <div className="mb-3">
                                        <label>PDF File <span className="text-danger">*</span></label>
                                        <input
                                            type="file"
                                            accept="application/pdf"
                                            ref={fileInputRef}
                                            className={`form-control ${formError.file ? "is-invalid" : ""}`}
                                            onChange={(e) => {
                                                const file = e.target.files?.[0];
                                                if (!file) return;

                                                if (file.type !== "application/pdf") {
                                                    setFormError((p) => ({
                                                        ...p,
                                                        file: "Only PDF files allowed",
                                                    }));
                                                    return;
                                                }

                                                if (file.size > 5 * 1024 * 1024) {
                                                    setFormError((p) => ({
                                                        ...p,
                                                        file: "File must be less than 5MB",
                                                    }));
                                                    return;
                                                }

                                                setFormData((p) => ({ ...p, file }));
                                                setFormError((p) => ({ ...p, file: "" }));
                                            }}
                                        />

                                        {formError.file && (
                                            <div className="invalid-feedback d-block">
                                                {formError.file}
                                            </div>
                                        )}
                                    </div>

                                    {/* EXISTING FILE DISPLAY — MUST BE HERE */}
                                    {modalMode === "edit" && editingRecord?.file_url && !formData.file && (
                                        <div className="mt-2">
                                            <label className="form-label">Current File</label>

                                            <div
                                                className="d-flex align-items-center gap-2 p-2 border rounded"
                                                style={{ cursor: "pointer" }}
                                                onClick={() =>
                                                    setPreview({
                                                        open: true,
                                                        url: `${REACT_APP_NODE_STATIC}${editingRecord.file_url}`,
                                                    })
                                                }
                                            >
                                                <i className="ti ti-file-text fs-20 text-primary" />
                                                <span className="text-primary">
                                                    {editingRecord.file_url.split("/").pop()}
                                                </span>
                                            </div>
                                        </div>
                                    )}

                                </div>

                                <div className="modal-footer d-flex justify-content-end gap-2">
                                    <button className="btn btn-light" onClick={hideModal}>
                                        Cancel
                                    </button>
                                    <button className="btn btn-primary" onClick={handleSubmit}>
                                        {modalMode === "add" ? "Add" : "Update"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>


                    {/* VIEW POLICY MODAL */}
                    <div className="modal fade" id="policy_view_modal">
                        <div className="modal-dialog modal-md modal-dialog-centered">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h4 className="modal-title">View Policy</h4>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                    />
                                </div>

                                <div className="modal-body">
                                    <div className="mb-3">
                                        <label>Title</label>
                                        <input
                                            className="form-control"
                                            value={viewData?.title || ""}
                                            readOnly
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label>Year</label>
                                        <input
                                            className="form-control"
                                            value={viewData?.year || ""}
                                            readOnly
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label>Uploaded Date</label>
                                        <input
                                            className="form-control"
                                            value={
                                                viewData
                                                    ? dayjs(viewData.created_at).format("DD-MMM-YYYY")
                                                    : ""
                                            }
                                            readOnly
                                        />
                                    </div>

                                    <div className="mb-3">
                                        <label>Document</label>
                                        {viewData?.file_url ? (
                                            <div className="d-flex gap-2 align-items-center">
                                                <a
                                                    href={`${REACT_APP_NODE_STATIC}${viewData.file_url}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="btn btn-outline-primary btn-sm"
                                                >
                                                    Preview PDF
                                                </a>

                                                <a
                                                    href={`${REACT_APP_NODE_STATIC}${viewData.file_url}`}
                                                    download
                                                    onClick={(e) => {
                                                        e.preventDefault();

                                                        const link = document.createElement("a");
                                                        link.href = `${REACT_APP_NODE_STATIC}${viewData.file_url}`;
                                                        link.download = viewData.title || "policy.pdf";
                                                        document.body.appendChild(link);
                                                        link.click();
                                                        document.body.removeChild(link);
                                                    }}
                                                    className="btn btn-outline-secondary btn-sm"
                                                >
                                                    Download
                                                </a>
                                            </div>
                                        ) : (
                                            <div>No file available</div>
                                        )}
                                    </div>
                                </div>

                                <div className="modal-footer">
                                    <button
                                        className="btn btn-light"
                                        data-bs-dismiss="modal"
                                    >
                                        Close
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/* PDF PREVIEW MODAL */}
                    <div
                        className={`modal ${preview.open ? "show d-block" : "fade"}`}
                        tabIndex={-1}
                        style={{ background: "rgba(0,0,0,0.6)" }}
                        onClick={() => setPreview({ open: false })}
                    >
                        <div className="modal-dialog modal-dialog-centered modal-xl" onClick={(e) => e.stopPropagation()}>
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">PDF Preview</h5>
                                    <button
                                        className="btn-close"
                                        onClick={() => setPreview({ open: false })}
                                    />
                                </div>

                                <div
                                    className="modal-body p-0"
                                    style={{
                                        height: "75vh",
                                    }}
                                >
                                    {preview.url && (
                                        <iframe
                                            src={`${preview.url}#toolbar=0`}
                                            width="100%"
                                            height="100%"
                                            style={{ border: "none" }}
                                            title="PDF Preview"
                                        />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>



                    {/* DELETE CONFIRMATION MODAL */}
                    <div id="top-modal" className="modal fade">
                        <div className="modal-dialog modal-top">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h4 className="modal-title">Delete Policy</h4>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        data-bs-dismiss="modal"
                                    />
                                </div>

                                <div className="modal-body">
                                    <p>Are you sure you want to delete this Policy?</p>
                                </div>

                                <div className="modal-footer d-flex justify-content-end gap-2">
                                    <button className="btn btn-light" data-bs-dismiss="modal">
                                        Cancel
                                    </button>

                                    <button
                                        className="btn btn-primary"
                                        data-bs-dismiss="modal"
                                        onClick={() => deleteId && handleDelete(deleteId)}
                                    >
                                        Delete
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    <Footer />
                </div>
            </HeaderFilterProvider>

            <ToastContainer position="top-right" autoClose={3000} />
        </>
    );
};

export default PolicyDocument;